// CartProvider — single source of truth for cart state.
// Simplified: no temp items, no enrichment, no reconciliation.
// /api/cart returns fully enriched data (shipping, seller info, PayPal).
"use client";

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { getSupabaseBrowserClient } from "@/utils/supabase/client";
import type { AuthChangeEvent } from "@supabase/supabase-js";
import type { CartItemData } from "@/components/cart/types";
import { cartEvents } from "@/lib/optimized-cart-events";
import { dedupCartRequest, dedupSyncLoginRequest, clearCartCache } from "@/lib/cart-request-deduplication";
import { DEFAULT_CURRENCY } from "@/lib/currencies";
import { addToCartAction } from "@/app/actions/cart";
import { AddToCartRejectedError } from "@/lib/cart/add-to-cart-rejection";
import { getGuestId } from "@/lib/guest";
import { deleteCartItem } from "@/lib/cart-quick-checkout";
import { fetchCartLines } from "@/lib/cart/fetch-cart-lines";
import { updateCartQuantity as updateCartQuantityBuilder } from "@/lib/cart/update-cart-quantity";
import { updateCartShippingMethod as updateCartShippingMethodBuilder } from "@/lib/cart/update-cart-shipping";
import { syncLoginCart } from "@/lib/cart/sync-login-cart";
import { syncLogoutCart } from "@/lib/cart/sync-logout-cart";
import { asRawImageRef, asRawImageRefs, type CartResponse } from "@kraahl/contract";

// --- Module-scope singletons (shared across HMR reloads, persist for tab lifetime) ---

let _cartSupabase: ReturnType<typeof getSupabaseBrowserClient> | null = null;
function getCartClient() {
  if (!_cartSupabase) _cartSupabase = getSupabaseBrowserClient();
  return _cartSupabase;
}

let globalSyncInProgress = false;

// --- Context ---

interface CartContextValue {
  cartItems: CartItemData[];
  isInitialized: boolean;
  isRefreshing: boolean;
  loadCart: (forceFresh?: boolean) => Promise<void>;
  addItem: (listingData: {
    listingId: string;
    name: string;
    price: number;
    currency?: string | null;
    images: string[];
    sellerId: string;
    sellerName: string;
    sellerAvatarUrl?: string;
    condition: string;
    description: string;
    shippingMethods?: any[];
    maxQuantity: number;
  }, opts?: { roomName?: string | null; messageId?: string | null }) => Promise<void>;
  updateQuantity: (id: string, qty: number) => Promise<void>;
  updateShippingMethod: (id: string, shippingId: string) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  clearLocalCart: () => void;
  refreshListingShippingMethods: (listingId: string) => Promise<void>;
  ensureFreshBeforeOpen: () => Promise<void>;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

// --- Provider ---

export function CartProvider({ userId, children }: { userId: string | null; children: ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItemData[]>([]);
  const itemOrderMap = useRef<Map<string, number>>(new Map());
  const itemOrderSeq = useRef(0);
  const lastUserId = useRef<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const operationInProgress = useRef<Set<string>>(new Set());
  const lastServerSync = useRef<number>(0);
  const loadTokenRef = useRef(0);
  const mutationQueue = useRef<Promise<void>>(Promise.resolve());
  // Lightweight tombstone: prevents zombie items when addItem's loadCart races with removeItem
  const removedListingIds = useRef<Map<string, number>>(new Map());

  const enqueueMutation = useCallback(async (task: () => Promise<void>) => {
    const run = mutationQueue.current.then(task).catch((err) => {
      console.error('[CART] Mutation failed in queue:', err);
    });
    mutationQueue.current = run.then(() => undefined);
    return run;
  }, []);

  // --- loadCart ---
  const loadCart = useCallback(async (forceFresh: boolean = false) => {
    const gid = getGuestId();
    const myToken = ++loadTokenRef.current;
    if (forceFresh) {
      setIsRefreshing(true);
      try { clearCartCache(); } catch {}
    }
    try {
      const fetchDirect = (): Promise<CartResponse> =>
        fetchCartLines({ userId, guestId: gid });
      const cartData = forceFresh ? await fetchDirect() : await dedupCartRequest(userId, gid, fetchDirect);
      if (myToken !== loadTokenRef.current) return;

      // 2026-07-04: is_unlisted, type, preorder_*, estimated_delivery and
      // min/max_orders are now PINNED in CartListingNestedSchema. They had
      // to be — Zod v4 `z.object` STRIPS unknown keys (the old "loose
      // passthrough" claim here was wrong), which silently killed the
      // unlisted gate and the preorder consent gate for months. If this
      // mapping ever reads a NEW listing column, pin it in the contract in
      // the same PR or it will read `undefined` forever.
      //
      // WS4.4 (2026-06-18): `shipping_mode` + `parcel_*` ARE now pinned in
      // `CartListingNestedSchema`. They're still read off `L` (the `any`
      // surface) because the rest of this mapping does too — extracting just
      // those into a narrowed handle would be churn for no win. The fields
      // become non-undefined on `CartItemData` (the route serialises `null`
      // for legacy listings), so `deriveShippingMode` sees a stable
      // discriminator.
      const serverItems: CartItemData[] = cartData.map((r) => {
        const L = r.listings as any;
        const parsedImages = Array.isArray(L.image_urls) ? L.image_urls : (typeof L.image_urls === 'string' ? JSON.parse(L.image_urls || '[]') : []);
        const shippingMethods = Array.isArray(L.shippingMethods) ? L.shippingMethods : [];
        return {
          id: r.id,
          listingId: L.id,
          sellerId: L.user_id,
          sellerName: L.sellerUsername || 'Unknown',
          sellerAvatarUrl: L.sellerAvatarUrl,
          sellerCheckoutNote: L.sellerCheckoutNote ?? null,
          name: L.title || 'Unknown Item',
          description: L.description || '',
          price: Number(L.price) || 0,
          currency: (typeof L.currency === "string" && L.currency ? L.currency : DEFAULT_CURRENCY),
          quantity: Math.max(1, r.quantity || 1),
          maxQuantity: Math.max(1, L.quantity || 1),
          images: parsedImages,
          condition: L.condition || 'used',
          grade: L.condition?.toLowerCase() === 'new' ? undefined : L.grade,
          shippingMethods,
          chatPrivateCancelledAt: L.chat_private_cancelled_at ?? null,
          isUnlisted: Boolean(L.is_unlisted),
          sellerBanned: Boolean(L.sellerBanned),
          selectedShippingMethod: L.selectedShippingMethod,
          updated_at: r.updated_at,
          paypalMerchantId: r.paypalMerchantId,
          paypalCapabilities: r.paypalCapabilities ?? [],
          availableProviders: Array.isArray(r.availableProviders) ? r.availableProviders : [],
          listingType: (L.type as "standard" | "preorder" | null) ?? null,
          preorderDeadline: L.preorder_deadline ?? null,
          estimatedDelivery: L.estimated_delivery ?? null,
          minOrders: L.min_orders ?? null,
          maxOrders: L.max_orders ?? null,
          preorderStatus: (L.preorder_status as "open" | "funded" | "in_production" | "shipped" | null) ?? null,
          // WS4.4 — Shippo discriminator + parcel dims. `null` for legacy
          // listings (route serialises `?? null`); `deriveShippingMode` treats
          // null/undefined as `'legacy'` so the existing flat-rate path stays
          // byte-for-byte unchanged. See .tondocs/shippo/PLAN.md §D WS4.
          shippingMode: (L.shipping_mode as "legacy" | "live_rate" | null) ?? null,
          parcelWeight: L.parcel_weight ?? null,
          parcelLength: L.parcel_length ?? null,
          parcelWidth: L.parcel_width ?? null,
          parcelHeight: L.parcel_height ?? null,
          // Slice C2 (2026-06-23): contract pins these to 'g' / 'mm' (or
          // null when legacy mode). Downstream `CartItemData` still accepts
          // the wider locale union for back-compat; the values flowing in
          // are now constants.
          parcelMassUnit: L.parcel_mass_unit ?? null,
          parcelDistanceUnit: L.parcel_distance_unit ?? null,
        } as CartItemData;
      });

      // Filter out recently removed items (prevents zombie reappearance)
      const now = Date.now();
      Array.from(removedListingIds.current.entries()).forEach(([lid, ts]) => {
        if (now - ts > 3000) removedListingIds.current.delete(lid);
      });
      const filteredItems = serverItems.filter(i => !removedListingIds.current.has(i.listingId));

      setCartItems(prev => {
        // Keep optimistic rows the server has not caught up with yet. A
        // `loadCart` that started BEFORE `addItem`'s write committed answers
        // without the new row; dropping it here would make the freshly added
        // item flicker out and back in. The pending row survives until a
        // response actually contains its listing.
        //
        // Derived into a NEW local (never by mutating `filteredItems`): this
        // updater must stay pure — React invokes it twice under StrictMode,
        // and an accumulating `concat` on the outer binding would duplicate
        // the row on the second pass.
        const serverListings = new Set(filteredItems.map(i => i.listingId));
        const unconfirmed = prev.filter(
          i => i.pending && !serverListings.has(i.listingId),
        );
        const items = unconfirmed.length > 0 ? [...filteredItems, ...unconfirmed] : filteredItems;

        // First load or empty: sort by date
        if (prev.length === 0 || itemOrderMap.current.size === 0) {
          const sorted = [...items].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
          itemOrderMap.current.clear();
          itemOrderSeq.current = 0;
          for (const it of sorted) {
            if (!itemOrderMap.current.has(it.listingId)) {
              itemOrderMap.current.set(it.listingId, itemOrderSeq.current++);
            }
          }
          return sorted;
        }

        // Subsequent loads: preserve existing order, append new items at end
        for (const it of items) {
          if (!itemOrderMap.current.has(it.listingId)) {
            itemOrderMap.current.set(it.listingId, itemOrderSeq.current++);
          }
        }

        // Clean up order map for removed items
        const knownListingIds = new Set(items.map(i => i.listingId));
        Array.from(itemOrderMap.current.keys()).forEach(lid => {
          if (!knownListingIds.has(lid)) itemOrderMap.current.delete(lid);
        });

        return [...items].sort((a, b) => {
          const orderA = itemOrderMap.current.get(a.listingId) ?? Number.POSITIVE_INFINITY;
          const orderB = itemOrderMap.current.get(b.listingId) ?? Number.POSITIVE_INFINITY;
          return orderA - orderB;
        });
      });

      lastServerSync.current = Date.now();
      setIsInitialized(true);
    } catch (error: any) {
      if (error instanceof Error && error.name === 'AbortError') console.warn('Cart loading timed out - keeping current state');
      else console.error('Error loading cart:', error);
      setIsInitialized(true);
    } finally {
      if (forceFresh) setIsRefreshing(false);
    }
  }, [userId]);

  // --- Auth + init effect ---
  useEffect(() => {
    async function handleLogin() {
      const { data: { session } } = await getCartClient().auth.getSession();
      const uid = session?.user?.id;
      if (!uid) return;
      if (globalSyncInProgress) {
        lastUserId.current = uid;
        if (!isInitialized) await loadCart();
        return;
      }
      if (lastUserId.current === uid) {
        if (!isInitialized) await loadCart();
        return;
      }
      globalSyncInProgress = true;
      try {
        const gid = getGuestId();
        await dedupSyncLoginRequest(uid, gid, () => syncLoginCart({ userId: uid, guestId: gid }));
        lastUserId.current = uid;
        await loadCart(true);
      } finally {
        globalSyncInProgress = false;
      }
    }

    async function handleLogout() {
      const uid = lastUserId.current;
      if (!uid) return;
      if (globalSyncInProgress) return;
      globalSyncInProgress = true;
      try {
        const gid = getGuestId();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        try {
          await syncLogoutCart({ userId: uid, guestId: gid, signal: controller.signal });
        } finally {
          clearTimeout(timeoutId);
        }
        lastUserId.current = null;
        await loadCart(true);
      } catch (error) {
        console.error('[CART] Logout sync failed:', error);
        lastUserId.current = null;
        await loadCart(true);
      } finally {
        globalSyncInProgress = false;
      }
    }

    const handleRefreshCart = () => { loadCart(); };

    if (userId && lastUserId.current !== userId) {
      handleLogin();
    } else if (!userId && lastUserId.current !== null) {
      handleLogout();
    } else {
      if (!isInitialized) loadCart();
    }

    const unsubscribeRefresh = cartEvents.onCartRefresh(handleRefreshCart);
    const { data: listener } = getCartClient().auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      if (event === 'SIGNED_IN') { const uid = session?.user?.id; if (uid && lastUserId.current !== uid) { handleLogin(); } }
      else if (event === 'SIGNED_OUT') { if (lastUserId.current) { handleLogout(); } }
    });

    return () => {
      listener.subscription.unsubscribe();
      unsubscribeRefresh();
    };
  }, [userId, loadCart, isInitialized]);

  // --- updateQuantity ---
  const updateQuantity = useCallback(async (id: string, qty: number) => {
    const items = cartItems;
    const item = items.find(i => i.id === id);
    if (!item || qty > item.maxQuantity || qty < 1) return;

    const operationKey = `qty-${id}`;
    if (operationInProgress.current.has(operationKey)) return;
    operationInProgress.current.add(operationKey);

    loadTokenRef.current++;
    const originalQuantity = item.quantity;
    setCartItems(prev => prev.map(i => i.id === id ? { ...i, quantity: qty } : i));

    try {
      await enqueueMutation(async () => {
        const gid = getGuestId();
        const uid = userIdRef.current;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        try {
          await updateCartQuantityBuilder({ id, qty, userId: uid, guestId: gid, signal: controller.signal });
        } finally {
          clearTimeout(timeoutId);
        }
      });
    } catch (error: any) {
      if (!(error instanceof Error && error.name === 'AbortError')) {
        console.error('Failed to update quantity:', error);
        setCartItems(prev => prev.map(i => i.id === id ? { ...i, quantity: originalQuantity } : i));
      }
    } finally {
      operationInProgress.current.delete(operationKey);
    }
  }, [cartItems, enqueueMutation]);

  // --- updateShippingMethod ---
  const updateShippingMethod = useCallback(async (id: string, shippingId: string) => {
    const items = cartItems;
    const item = items.find(i => i.id === id);
    const newMethod = item?.shippingMethods.find(m => m.id === shippingId) ?? null;

    loadTokenRef.current++;
    setCartItems(prev => prev.map(i => i.id === id ? { ...i, selectedShippingMethod: newMethod } : i));

    try {
      await enqueueMutation(async () => {
        const gid = getGuestId();
        const uid = userIdRef.current;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        try {
          await updateCartShippingMethodBuilder({ id, shippingId, userId: uid, guestId: gid, signal: controller.signal });
        } finally {
          clearTimeout(timeoutId);
        }
      });
    } catch (error: any) {
      if (!(error instanceof Error && error.name === 'AbortError')) {
        console.error('Failed to update shipping method:', error);
        setCartItems(prev => prev.map(i => i.id === id ? { ...i, selectedShippingMethod: item?.selectedShippingMethod ?? null } : i));
      }
    }
  }, [cartItems, enqueueMutation]);

  // Ref for auto-select effect to avoid re-firing on function identity change
  const updateShippingMethodRef = useRef(updateShippingMethod);
  updateShippingMethodRef.current = updateShippingMethod;

  // --- addItem ---
  const addItem = async (listingData: {
    listingId: string; name: string; price: number; currency?: string | null;
    images: string[]; sellerId: string; sellerName: string; sellerAvatarUrl?: string;
    condition: string; description: string; shippingMethods?: any[]; maxQuantity: number;
  }, opts?: { roomName?: string | null; messageId?: string | null }) => {
    const operationKey = `add-${listingData.listingId}`;
    if (operationInProgress.current.has(operationKey)) return;
    operationInProgress.current.add(operationKey);

    let succeeded = false;
    try {
      // If item already in cart, just increment quantity
      const existingItem = cartItems.find(item => item.listingId === listingData.listingId);
      if (existingItem) {
        const newQty = existingItem.quantity + 1;
        if (newQty <= existingItem.maxQuantity) await updateQuantity(existingItem.id, newQty);
        return;
      }

      loadTokenRef.current++;
      // Clear any removal tombstone for this listing (user is re-adding)
      removedListingIds.current.delete(listingData.listingId);

      // Render the row from what the click ALREADY knows, before the network.
      // The add used to be fully blocking — server action, then a forced
      // `loadCart`, then (for single-method listings) the shipping auto-select
      // PUT — roughly 1.16s on staging before the button stopped spinning.
      //
      // Everything the server derives (availableProviders, shippingMode,
      // parcel dims, paypalMerchantId, the real row id) is deliberately absent
      // here rather than guessed; `pending` marks the row as not-yet-decidable
      // for consumers. The reconcile below fills it in.
      const optimisticId = `optimistic:${listingData.listingId}`;
      const optimisticRow: CartItemData = {
        id: optimisticId,
        listingId: listingData.listingId,
        sellerId: listingData.sellerId,
        sellerName: listingData.sellerName || 'Unknown',
        // The caller hands these over AS STORED (bucket-relative) — same shape
        // the cart route serialises — so they are branded, not resolved, here.
        sellerAvatarUrl: asRawImageRef(listingData.sellerAvatarUrl),
        name: listingData.name || 'Unknown Item',
        description: listingData.description || '',
        price: Number(listingData.price) || 0,
        currency: listingData.currency || DEFAULT_CURRENCY,
        quantity: 1,
        maxQuantity: Math.max(1, listingData.maxQuantity || 1),
        images: asRawImageRefs(Array.isArray(listingData.images) ? listingData.images : []),
        condition: listingData.condition,
        shippingMethods: Array.isArray(listingData.shippingMethods) ? listingData.shippingMethods : [],
        selectedShippingMethod: null,
        updated_at: new Date().toISOString(),
        pending: true,
      };
      setCartItems(prev => [...prev, optimisticRow]);

      // Server-side add (handles stock validation, chat-private auth, and the
      // atomic write — see lib/cart-perform-add.ts)
      const gid = getGuestId();
      const uid = userIdRef.current;
      const fd = new FormData();
      fd.set("listingId", listingData.listingId);
      fd.set("qty", "1");
      if (uid) fd.set("userId", uid); else fd.set("guestId", gid);
      if (opts?.roomName) fd.set("roomName", opts.roomName);
      if (opts?.messageId) fd.set("messageId", opts.messageId);

      try {
        const added = await addToCartAction(fd);
        // A refusal is a VALUE now, so it survives the server-action boundary
        // with its sentence intact. It used to be a throw, which Next redacts
        // in production — the buyer saw "An error occurred in the Server
        // Components render…" instead of "this item is unavailable"
        // (incident 2026-08-09). Rethrown as a typed error so the existing
        // rollback path below stays the single place that undoes the row.
        if (added.kind === "rejected") {
          throw new AddToCartRejectedError(added.rejection);
        }
        // The server row already holds every unit this buyer can have — the
        // click was a no-op, not a failure. Drop the speculative row and let
        // the reconcile below surface the real one. (This is the branch the
        // buyer in incident 2026-08-09 hit repeatedly, when the shortcut at
        // the top of `addItem` had no loaded `cartItems` to match against.)
        if (added.kind === "unchanged") {
          setCartItems(prev => prev.filter(i => i.id !== optimisticId));
          void loadCart(true);
          succeeded = true;
          return;
        }
        // Adopt the real row id and the quantity the server actually settled
        // on (it clamps to stock, and merges with rows we could not see).
        // Still `pending` — the enriched fields only arrive with loadCart.
        setCartItems(prev =>
          prev.map(i =>
            i.id === optimisticId
              ? { ...i, id: added.cartItemId, listingId: added.listingId, quantity: added.quantity }
              : i,
          ),
        );
      } catch (error) {
        // Roll back structurally, by id — never by restoring a `prev` snapshot,
        // which would also undo any concurrent add/remove that landed meanwhile
        // (.tondocs/skills/concurrency-design.md §9.2).
        setCartItems(prev => prev.filter(i => i.id !== optimisticId));
        throw error;
      }

      // Reconcile in the background. The caller (useAddToCart → the button)
      // is released as soon as the write is durable; the row is already on
      // screen and only gains server-derived fields when this resolves.
      void loadCart(true);
      succeeded = true;
    } finally {
      if (succeeded) {
        setTimeout(() => operationInProgress.current.delete(operationKey), 500);
      } else {
        operationInProgress.current.delete(operationKey);
      }
    }
  };

  // --- removeItem ---
  const removeItem = async (id: string) => {
    const item = cartItems.find(i => i.id === id);
    if (!item) { console.warn('[CART] Cannot remove item - not found:', id); return; }

    loadTokenRef.current++;

    // All items for the same listing (server may have duplicates)
    const idsToRemove = cartItems.filter(i => i.listingId === item.listingId).map(i => i.id);

    // Mark listing as removed (prevents zombie reappearance from concurrent loadCart)
    removedListingIds.current.set(item.listingId, Date.now());

    // Optimistic remove
    setCartItems(prev => {
      itemOrderMap.current.delete(item.listingId);
      return prev.filter(i => i.listingId !== item.listingId);
    });

    try {
      const gid = getGuestId();
      const uid = userIdRef.current;
      for (const remId of idsToRemove) {
        await enqueueMutation(async () => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          const { ok } = await deleteCartItem({
            id: remId,
            userId: uid ?? undefined,
            guestId: gid ?? undefined,
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          if (!ok) throw new Error(`Remove failed`);
        });
      }
      // Server confirmed removal — clear tombstone
      removedListingIds.current.delete(item.listingId);
    } catch (error) {
      console.error('[CART] Remove failed:', error);
      removedListingIds.current.delete(item.listingId);
      await loadCart(true);
    }
  };

  // Auto-select shipping method when only one option is available
  const autoSelectedShippingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const item of cartItems) {
      // Never auto-select against a pending row: its `id` is either the local
      // `optimistic:` placeholder or a real id whose server-side shipping
      // selection has not been read yet. Writing either would PUT a row the
      // API cannot match, or clobber a choice the server already holds.
      if (item.pending) continue;
      if (
        !item.selectedShippingMethod &&
        Array.isArray(item.shippingMethods) &&
        item.shippingMethods.length === 1 &&
        !autoSelectedShippingRef.current.has(item.id)
      ) {
        autoSelectedShippingRef.current.add(item.id);
        updateShippingMethodRef.current(item.id, item.shippingMethods[0].id);
      }
    }
  }, [cartItems]);

  const ensureFreshBeforeOpen = useCallback(async () => {
    const FRESH_WINDOW = 800;
    if (Date.now() - lastServerSync.current > FRESH_WINDOW) {
      await loadCart(true);
    }
  }, [loadCart]);

  const clearLocalCart = useCallback(() => {
    try { clearCartCache(); } catch {}
    autoSelectedShippingRef.current.clear();
    removedListingIds.current.clear();
    loadTokenRef.current++;
    setCartItems([]);
    setTimeout(() => loadCart(true), 300);
  }, [loadCart]);

  // Reload cart to get fresh shipping data for a listing
  const refreshListingShippingMethods = useCallback(async (_listingId: string) => {
    await loadCart(true);
  }, [loadCart]);

  return (
    <CartContext.Provider value={{
      cartItems, isInitialized, isRefreshing, loadCart, addItem,
      updateQuantity, updateShippingMethod, removeItem, clearLocalCart,
      refreshListingShippingMethods, ensureFreshBeforeOpen,
    }}>
      {children}
    </CartContext.Provider>
  );
}

// --- Hook ---

export function useCartContext(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCartContext must be used within a CartProvider");
  return context;
}
